import React, { useState } from 'react';
import { useNavigate, Link, Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { API_URL } from '../config';
import '../styles/Auth.css';

interface RecentAccount {
  name: string;
  email: string;
}

const Login: React.FC = () => {
  const { user, login } = useAuth();
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [selectedAccount, setSelectedAccount] = useState<RecentAccount | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [recentAccounts, setRecentAccounts] = useState<RecentAccount[]>([]);
  const navigate = useNavigate();

  React.useEffect(() => {
    const saved = localStorage.getItem('recent_accounts');
    if (saved) {
      setRecentAccounts(JSON.parse(saved));
    } else {
      const defaults = [
        { name: 'Ian Connell', email: 'IanConnell128@gmail.com' },
        { name: 'Fiona Macdonald', email: 'fiona.macdonald@gmail.com' },
        { name: 'Nevaeh Quartermain', email: 'nevaeh.quartermain@icloud.com' },
        { name: 'Denise Dawson', email: 'denise.dawson@yahoo.com' }
      ];
      setRecentAccounts(defaults);
    }
  }, []);

  if (user) {
    return <Navigate to="/" replace />;
  }

  const handleSelectAccount = (acc: RecentAccount) => {
    setSelectedAccount(acc);
    setFormData({ ...formData, email: acc.email });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const res = await fetch(`${API_URL}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });
      const data = await res.json();
      if (res.ok) {
        const newAccount = { name: data.user.name, email: data.user.email };
        const updated = [newAccount, ...recentAccounts.filter(a => a.email !== data.user.email)].slice(0, 4);
        localStorage.setItem('recent_accounts', JSON.stringify(updated));
        login(data);
        navigate('/');
      } else {
        setError(data.error || 'Login failed');
      }
    } catch (err) {
      setError('Connection error');
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-card">
        <h1>{selectedAccount ? `Welcome, ${selectedAccount.name.split(' ')[0]}` : 'Login'}</h1>
        <p>{selectedAccount ? 'Please enter your password to continue' : 'Welcome back to The F.I.N.D.ca'}</p>
        
        {!selectedAccount && recentAccounts.length > 0 && (
          <div className="recent-logins" style={{ marginBottom: '20px', textAlign: 'center' }}>
            <p style={{ fontSize: '0.85rem', color: '#666', marginBottom: '10px' }}>Previous Logins:</p>
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'center', flexWrap: 'wrap' }}>
              {recentAccounts.map(acc => (
                <button 
                  key={acc.email}
                  onClick={() => handleSelectAccount(acc)}
                  style={{
                    padding: '8px 16px',
                    borderRadius: '20px',
                    border: '1px solid var(--primary-color)',
                    background: '#fff',
                    color: 'var(--primary-color)',
                    fontSize: '0.9rem',
                    fontWeight: 600,
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    boxShadow: 'var(--shadow-subtle)'
                  }}
                >
                  {acc.name.split(' ')[0]}
                </button>
              ))}
            </div>
          </div>
        )}

        {error && <div className="auth-error">{error}</div>}
        <form onSubmit={handleSubmit} className="auth-form">
          {!selectedAccount ? (
            <div className="form-group">
              <label>Email Address</label>
              <input type="email" placeholder="john@example.com" value={formData.email} onChange={e => setFormData({...formData, email: e.target.value})} required />
            </div>
          ) : (
            <div style={{ textAlign: 'center', marginBottom: '20px' }}>
              <span style={{ fontSize: '0.9rem', color: '#666' }}>{selectedAccount.email}</span>
              <button 
                type="button" 
                onClick={() => setSelectedAccount(null)}
                style={{ background: 'none', border: 'none', color: 'var(--primary-color)', fontSize: '0.8rem', marginLeft: '10px', cursor: 'pointer', textDecoration: 'underline' }}
              >
                Switch Account
              </button>
            </div>
          )}
          
          <div className="form-group">
            <label>Password</label>
            <div className="password-input-wrapper">
              <input 
                type={showPassword ? "text" : "password"} 
                placeholder="••••••" 
                autoFocus={!!selectedAccount}
                value={formData.password} 
                onChange={e => setFormData({...formData, password: e.target.value})} 
                required 
              />
              <button 
                type="button" 
                className="password-toggle" 
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? '👁️‍G' : '👁️'}
              </button>
            </div>
          </div>
          <button type="submit" className="auth-btn">Login</button>
        </form>
        <p className="auth-footer">Don't have an account? <Link to="/signup">Sign Up</Link></p>
      </div>
    </div>
  );
};

export default Login;
